/**
 * Call logs — HX activity view. Segmented filters, polished table with
 * status chips and one-click call-back.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCall, formatDuration } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import DataTable from "@/components/DataTable";
import { DirectionPill, StatusPill, timeAgo } from "./Dashboard";
import { Microphone, Phone, Eye, ListBullets } from "@phosphor-icons/react";
import { HxCard, HxChip, HX_COLORS as C } from "@/hx";

export default function CallLogs() {
  const [calls, setCalls] = useState([]);
  const [filter, setFilter] = useState("all");
  const { startCall } = useCall();
  const { user } = useAuth();
  const isOversight = user?.role === "admin" || user?.role === "supervisor";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/calls?limit=200");
      setCalls(data);
    })();
  }, []);

  const filtered = useMemo(() => calls.filter((c) => {
    if (filter === "inbound" && c.direction !== "inbound") return false;
    if (filter === "outbound" && c.direction !== "outbound") return false;
    if (filter === "missed" && c.status !== "missed") return false;
    if (filter === "recorded" && !c.recorded) return false;
    return true;
  }), [calls, filter]);

  const columns = [
    {
      key: "to_name", label: "Contact", sortable: true,
      render: (c) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{c.to_name}</div>
          <div style={{ fontSize: 10.5, color: "var(--hx-ink-3)", fontFamily: "var(--hx-mono)" }}>{c.to_number}</div>
        </div>
      ),
    },
    ...(isOversight ? [{ key: "agent_name", label: "Agent", sortable: true, width: "140px", render: (c) => <span style={{ fontSize: 13, fontWeight: 600 }}>{c.agent_name || "—"}</span> }] : []),
    { key: "from_number", label: "From",      sortable: true, width: "150px", render: (c) => <span style={{ fontSize: 12, fontFamily: "var(--hx-mono)" }}>{c.from_number}</span> },
    { key: "direction",   label: "Direction", sortable: true, width: "110px", render: (c) => <DirectionPill d={c.direction} /> },
    { key: "duration",    label: "Duration",  sortable: true, width: "100px", align: "right", render: (c) => <span className="tabular-nums">{c.duration ? formatDuration(c.duration) : "—"}</span> },
    { key: "status",      label: "Status",    sortable: true, width: "110px", render: (c) => <StatusPill s={c.status} /> },
    { key: "recorded",    label: "Recording", sortable: true, width: "100px", render: (c) => c.recorded ? <Microphone size={16} color={C.blue} /> : <span style={{ color: "var(--hx-ink-3)" }}>—</span> },
    { key: "started_at",  label: "When",      sortable: true, width: "110px", render: (c) => <span style={{ fontSize: 12, color: "var(--hx-ink-2)" }}>{timeAgo(c.started_at)}</span> },
    {
      key: "_act", label: "", width: "90px",
      render: (c) => (
        <button
          onClick={() => startCall({ to_number: c.to_number, to_name: c.to_name })}
          className="hx-dial__call"
          data-testid={`callback-${c.id}`}
          title="Call back"
        >
          <Phone size={12} weight="fill" /> Call
        </button>
      ),
    },
  ];

  return (
    <div className="hbf-hx" data-testid="call-logs-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up">
        <div>
          <div className="hx-hero__eyebrow"><ListBullets size={16} weight="duotone" color={C.blue} /> Activity</div>
          <h1 className="hx-hero__title">Call <em>logs</em></h1>
          <p className="hx-hero__sub">{calls.length} calls in the last 7 days — every conversation, tracked.</p>
        </div>
        <div className="hx-hero__cta" style={{ alignItems: "flex-end", flexDirection: "column", gap: 10 }}>
          {isOversight && (
            <HxChip tone="blue" data-testid="calllogs-oversight-chip"><Eye size={11} weight="bold" /> Viewing all agents</HxChip>
          )}
          <div className="hx-seg" role="tablist">
            {["all", "inbound", "outbound", "missed", "recorded"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`hx-seg__btn ${filter === f ? "hx-seg__btn--on" : ""}`}
                style={{ textTransform: "capitalize" }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="hx-up hx-d1">
        {calls.length === 0 ? (
          <HxCard>
            <EmptyState
              icon="phone"
              title="No calls yet"
              description="Place your first call from the dialer to start tracking your call history, recordings, and analytics."
              primaryAction={{ label: "Open dialer", onClick: () => (window.location.href = "/app/dialer") }}
              secondaryAction={{ label: "Add a contact", onClick: () => (window.location.href = "/app/contacts") }}
              learnMore={{ label: "How calling works", href: "/developers" }}
              testId="empty-state-calls"
            />
          </HxCard>
        ) : (
          <DataTable
            testId="calls-log-table"
            data={filtered}
            rowKey={(c) => c.id}
            rowTestId={(c) => `call-row-${c.id}`}
            searchPlaceholder="Search by name or number…"
            searchKeys={["to_name", "to_number", "from_number", "agent_name"]}
            columns={columns}
          />
        )}
      </div>
    </div>
  );
}
